import { PAYMENT_PROOF_LIMITS, type PaymentProof } from "@teamna/shared";
import { useState } from "react";
import { Button, FileField, InlineError, PaperclipIcon, showToast } from "../../components/index.ts";
import {
  PAYMENT_PROOF_ACCEPT,
  useRemovePaymentProof,
  useSetPaymentProof,
  useUploadPaymentProof,
} from "@teamna/web-client/hooks/use-owner.ts";

/**
 * "Attach proof of payment" on Record a payment (#ai-feedback 2026-10-26, card #2): one photo or
 * PDF of the receipt or the bank transfer. The file goes up as soon as it is chosen, so Record only
 * waits for the payment. A second file replaces the first. The payment call sends `proofId`.
 */
export type ProofUpload = {
  /** The id to send with the payment, when the file is up. */
  proofId: string | undefined;
  uploading: boolean;
  fileName: string | undefined;
  error: string | undefined;
  pick: (file: File) => void;
  clear: () => void;
};

/** Why the server would refuse this file, before it goes up. Null when it is a good file. */
export function proofFileProblem(file: File): string | null {
  if (!PAYMENT_PROOF_ACCEPT.split(",").includes(file.type)) return "Add a photo (JPG or PNG) or a PDF.";
  if (file.size === 0) return "The file is empty. Choose another file.";
  if (file.size > PAYMENT_PROOF_LIMITS.fileBytes) return "The file can be 10 MB or less.";
  return null;
}

export function useProofUpload(businessId: string): ProofUpload {
  const upload = useUploadPaymentProof(businessId);
  const [proof, setProof] = useState<PaymentProof | null>(null);
  const [picked, setPicked] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);

  return {
    proofId: proof?.id,
    uploading: upload.isPending,
    fileName: proof?.fileName ?? picked,
    error,
    pick(file) {
      setProof(null);
      const problem = proofFileProblem(file);
      setError(problem ?? undefined);
      if (problem) {
        setPicked(undefined);
        return;
      }
      setPicked(file.name);
      upload.mutate(file, {
        onSuccess: (saved) => setProof(saved),
        onError: () => {
          setPicked(undefined);
          setError("The file did not upload. Try again.");
        },
      });
    },
    clear() {
      upload.reset();
      setProof(null);
      setPicked(undefined);
      setError(undefined);
    },
  };
}

export function ProofField({ upload, memberName }: { upload: ProofUpload; memberName: string }) {
  return (
    <div className="flex flex-col gap-2">
      <FileField
        label="Attach proof of payment"
        hint={`Optional. A photo or a PDF of the receipt or the transfer, up to 10 MB. ${memberName} does not see it. It shows in Payments.`}
        accept={PAYMENT_PROOF_ACCEPT}
        buttonLabel={upload.uploading ? "Uploading the file" : upload.fileName ? "Choose another file" : "Take a photo or choose a file"}
        disabled={upload.uploading}
        error={upload.error}
        {...(upload.fileName ? { fileName: upload.fileName } : {})}
        showFileName={Boolean(upload.fileName)}
        onFile={upload.pick}
      />
      {upload.fileName && !upload.uploading ? (
        <div>
          <Button variant="ghost" size="sm" onClick={upload.clear}>
            Remove the file
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/** The link that opens a proof in a new tab, through its signed URL. */
export function ProofLink({ proof, compact = false }: { proof: PaymentProof; compact?: boolean }) {
  if (!proof.url) return null;
  return (
    <a
      href={proof.url}
      target="_blank"
      rel="noopener noreferrer"
      // In a table row that opens a panel, the link only opens the file.
      onClick={(event) => event.stopPropagation()}
      className="inline-flex items-center gap-1.5 font-semibold text-accent-strong underline-offset-2 hover:underline focus:outline-none focus-visible:outline-3 focus-visible:outline-accent-soft"
      aria-label={compact ? `Open the proof of payment, ${proof.fileName}` : undefined}
    >
      <PaperclipIcon size={16} />
      {compact ? null : <span className="max-w-[220px] truncate">{proof.fileName}</span>}
    </a>
  );
}

/**
 * The proof of a payment recorded before, on the Payments page: open it, and for the owner and the
 * admins attach, replace or remove it. A new file goes up first, then takes the place of the old one.
 */
export function ProofEditor({
  businessId,
  paymentId,
  proof,
  canChange,
}: {
  businessId: string;
  paymentId: string;
  proof: PaymentProof | null;
  canChange: boolean;
}) {
  const upload = useUploadPaymentProof(businessId);
  const set = useSetPaymentProof(businessId);
  const remove = useRemovePaymentProof(businessId);
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const busy = upload.isPending || set.isPending;

  async function replace(file: File) {
    const refused = proofFileProblem(file);
    setProblem(refused ?? undefined);
    if (refused) return;
    try {
      const saved = await upload.mutateAsync(file);
      await set.mutateAsync({ paymentId, proofId: saved.id });
      showToast(proof ? "The proof of payment is replaced." : "The proof of payment is attached.");
    } catch {
      // The error shows under the field.
    }
  }

  return (
    <div className="flex flex-col gap-3 py-2.5">
      <div className="flex items-baseline justify-between gap-4">
        <span className="text-[14px] text-ink-2">Proof of payment</span>
        {proof ? <ProofLink proof={proof} /> : <span className="text-[14px] font-semibold text-ink">None</span>}
      </div>
      {canChange ? (
        <div className="flex flex-col gap-2">
          <FileField
            label={proof ? "Replace the proof" : "Attach proof of payment"}
            hideLabel
            hint="A photo or a PDF of the receipt or the transfer, up to 10 MB."
            accept={PAYMENT_PROOF_ACCEPT}
            buttonLabel={busy ? "Uploading the file" : proof ? "Replace the file" : "Attach proof of payment"}
            disabled={busy || remove.isPending}
            error={problem}
            showFileName={false}
            onFile={(file) => void replace(file)}
          />
          {proof ? (
            <div>
              <Button
                variant="ghost"
                size="sm"
                loading={remove.isPending}
                disabled={busy}
                onClick={() => remove.mutate({ paymentId }, { onSuccess: () => showToast("The proof of payment is removed.") })}
              >
                Remove the proof
              </Button>
            </div>
          ) : null}
          {upload.error || set.error || remove.error ? <InlineError error={upload.error ?? set.error ?? remove.error} /> : null}
        </div>
      ) : null}
    </div>
  );
}
